import { Component } from 'react';

import { LocalStorage } from 'saddlebag-localstorage';

import BpkText, {
  TEXT_COLORS,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import { buildSearchSubmitButtonAttributes } from '../utils/meaningful-behaviour-data';
import { buildAdditionalInfoDayViewSearch } from '../utils/mini-events';

import AutosuggestDataProvider from './AutosuggestDataProvider';
import ChappedLayoutSEO from './ChappedLayoutSEO';
import ExpandableLayout from './ExpandableLayout';
import HorizontalLayout from './HorizontalLayout';

import type { SearchDestination, SearchSuggestion } from '../types';
import type { MBDProps } from '@shared/types/frontend/meaningful-behaviour-data';
import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { I18nService } from '@shared/types/i18n';
import type { UserContext } from '@shared/types/user-context';
import type { Maybe, ObjectMapping, Stay } from '@shared/types/utils';
import type { Destination } from '@shared/ui/types';

import STYLES from './SearchControls.module.scss';

import {
  SEARCH_CLICKED,
  SEARCH_SUMITTED,
  SEARCH_VALIDATION_FAILED,
} from '@shared/constants/newrelic-events/event-names';
import PageTypes from '@shared/constants/page-types';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';
import logger from '@shared/utils-frontend/logger';
import {
  ACTION_TYPE,
  ENTITY_TYPE,
  CHANGED_CONDITION,
} from '@shared/utils-frontend/mini-events/constants';
import { buildAdditionalInfoSearchConditionChanged } from '@shared/utils-frontend/mini-events/hotels-action';
import {
  defaultGuestsAndRooms,
  stayLength,
} from '@shared/utils-universal/guest-stay';

const cls = cssModules(STYLES);

const LAYOUT: ObjectMapping = {
  CHAPPED: 'chapped',
  HORIZONTAL: 'horizontal',
  EXPANDABLE: 'expandable',
};

const SEARCH_ACTION_TYPE: ObjectMapping = {
  [PageTypes.SEARCH]: ACTION_TYPE.DAY_VIEW_SEARCH,
  [PageTypes.DETAILS]: ACTION_TYPE.HOTEL_DETAIL_SEARCH,
  [PageTypes.DEALS]: ACTION_TYPE.HOTEL_DEALS_SEARCH,
};

type Props = {
  i18n: I18nService;
  miniEventTracker: MiniEventTracker;
  onSubmit: Function;
  submittingForm?: boolean;
  destinationLabel: string;
  lightLabel?: boolean;
  freeCancellation?: boolean;
  /* eslint-disable react/no-unused-prop-types */
  destination?: Destination;
  stay?: Stay;
  prefilledDestination?: Destination;
  showPreference?: boolean;
  /* eslint-enable react/no-unused-prop-types */
  arrangeInline?: boolean;
  masonry?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  searchWithChildren?: boolean;
  shouldAutoSearch?: boolean;
  className?: string;
  layout?: keyof typeof LAYOUT;
  userContext: UserContext;
  hideLabel?: boolean;
  isFromSeoPage?: boolean;
};

type State = {
  destination?: Destination | SearchSuggestion;
  checkInDate: Maybe<Date>;
  checkOutDate: Maybe<Date>;
  adults: number;
  rooms: number;
  childrenAges: number[];
  freeCancellation?: Maybe<boolean>;
  showPreference?: boolean;
  currentProps: {
    destination?: Destination;
    stay: Maybe<Stay>;
    prefilledDestination?: Destination;
  };
  urlFilters: null;
  miniEventFilters: null;
  destinationInput: Maybe<string>;
  validDestination: boolean;
};

const defaultProps = {
  submittingForm: false,
  lightLabel: true,
  freeCancellation: false,
  arrangeInline: false,
  masonry: false,
  shouldAutoSearch: false,
};

const localStorage = new LocalStorage('hotelsWebsiteLocalStorage');
const buildSearchState = (props: Props, state?: State) => {
  const {
    destination,
    freeCancellation,
    prefilledDestination,
    showPreference,
    stay,
  } = props;
  let checkIn;
  let checkOut;
  let childrenAges: number[] = [];
  let { numberOfAdults, numberOfRooms } = defaultGuestsAndRooms();
  if (stay) {
    ({ checkIn, checkOut, childrenAges, numberOfAdults, numberOfRooms } = stay);
  }

  const { destination: existingDestination } = state || {};

  return {
    destination: destination || existingDestination || prefilledDestination,
    checkInDate: checkIn,
    checkOutDate: checkOut,
    adults: numberOfAdults,
    rooms: numberOfRooms,
    childrenAges,
    freeCancellation,
    showPreference,
    currentProps: {
      destination,
      prefilledDestination,
      stay,
    },
  };
};

const searchPropsAreChanging = (nextProps: Props, prevState: State) => {
  const {
    destination: nextDestination,
    prefilledDestination: nextPrefilledDestination,
    stay: nextStay,
  } = nextProps;
  let nextCheckIn;
  let nextCheckOut;
  // @ts-expect-error Property 'nextNumberOfAdults' and 'nextNumberOfRooms' do not exist on type
  // TODO: need to be fixed, it's weird usage and might be caused bugs
  let { nextNumberOfAdults, nextNumberOfRooms } = defaultGuestsAndRooms();

  if (nextStay) {
    ({
      checkIn: nextCheckIn,
      checkOut: nextCheckOut,
      numberOfAdults: nextNumberOfAdults,
      numberOfRooms: nextNumberOfRooms,
    } = nextStay);
  }

  if (!nextCheckIn && !nextCheckOut) {
    return false;
  }

  const { entity: nextEntity, entityId: nextEntityId } = nextDestination || {};
  const { entityId: nextPrefilledEntityId } = nextPrefilledDestination || {};

  const {
    destination,
    prefilledDestination,
    stay: prevStay,
  } = prevState.currentProps;
  let checkIn;
  let checkOut;
  let { numberOfAdults, numberOfRooms } = defaultGuestsAndRooms();

  if (prevStay) {
    ({ checkIn, checkOut, numberOfAdults, numberOfRooms } = prevStay);
  }
  const { entityId: prefilledEntityId } = prefilledDestination || {};
  const { entity, entityId } = destination || {};

  return (
    entityId !== nextEntityId ||
    entity !== nextEntity ||
    prefilledEntityId !== nextPrefilledEntityId ||
    checkIn !== nextCheckIn ||
    checkOut !== nextCheckOut ||
    numberOfAdults !== nextNumberOfAdults ||
    numberOfRooms !== nextNumberOfRooms
  );
};

const hasDestination = (destination?: Destination) =>
  !!(destination && destination.entityId);

class SearchControls extends Component<Props, State> {
  static defaultProps = defaultProps;

  searchItems: any[];

  mbdProps: MBDProps;

  constructor(props: Props) {
    super(props);

    this.state = {
      destinationInput: null,
      ...buildSearchState(props),
      urlFilters: null,
      miniEventFilters: null,
      validDestination: true,
    };

    this.searchItems = [];
    this.mbdProps = buildSearchSubmitButtonAttributes();
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    if (searchPropsAreChanging(nextProps, prevState)) {
      return buildSearchState(nextProps, prevState);
    }

    return null;
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      adults,
      checkInDate,
      checkOutDate,
      childrenAges,
      destination,
      rooms,
    } = this.state;
    const { userContext } = this.props;

    if (
      destination === prevState.destination &&
      checkInDate === prevState.checkInDate &&
      checkOutDate === prevState.checkOutDate &&
      adults === prevState.adults &&
      childrenAges === prevState.childrenAges &&
      rooms === prevState.rooms &&
      userContext.pageType === prevProps.userContext.pageType
    ) {
      return;
    }

    this.updateMbdProps();
  }

  onDestinationSuggestionsRequested = ({ value }: { value: string }) => {
    this.setState({ destinationInput: value });
  };

  onChangeInputValue = (value: string) => {
    const { destinationInput } = this.state;
    if (destinationInput !== value) {
      this.setState({ destinationInput: value });
    }
  };

  onDestinationChange = ({ suggestion }: { suggestion: SearchSuggestion }) => {
    this.setState(
      {
        destination: suggestion,
      },
      () => this.validateForm(),
    );
  };

  onRecentSearchesChange = (item: SearchDestination) => {
    const { checkInDate: checkInState, checkOutDate: checkOutState } =
      this.state;
    const {
      adults,
      checkInDate: checkInRecent,
      checkOutDate: checkOutRecent,
      childrenAges,
      rooms,
    } = item;

    if (checkInRecent) {
      const futureDate = stayLength({
        checkIn: new Date(),
        checkOut: checkInRecent,
      });
      const checkInDate = futureDate >= 0 ? checkInRecent : checkInState;
      const checkOutDate = futureDate >= 0 ? checkOutRecent : checkOutState;
      this.onDatesChanged(checkInDate, checkOutDate);
      this.setState({
        adults,
        childrenAges,
        rooms,
      });
    }
  };

  onDatesChanged = (checkInDate: Maybe<Date>, checkOutDate: Maybe<Date>) => {
    this.setState({
      checkInDate,
      checkOutDate,
    });
  };

  onDatesRangeApply = (checkInDate: Date, checkOutDate: Date) => {
    this.onDatesChanged(checkInDate, checkOutDate);
    if (this.props.shouldAutoSearch) {
      this.traceConditionChangedEvent({
        changedCondition: CHANGED_CONDITION.DATE_RANGE,
      });
      setTimeout(() => {
        this.triggerSearch();
      }, 0);
    }
  };

  onDatesRangeApplyNoAutoSearch = (checkInDate: Date, checkOutDate: Date) => {
    this.onDatesChanged(checkInDate, checkOutDate);
    this.traceConditionChangedEvent({
      changedCondition: CHANGED_CONDITION.DATE_RANGE,
    });
  };

  getGuestsRoomsChildren = ({
    adultChanged,
    adults,
    childrenAges,
    childrenAgesChanged,
    roomNumChanged,
    rooms,
  }: {
    rooms: number;
    adults: number;
    childrenAges: number[];
    adultChanged?: Maybe<boolean>;
    childrenAgesChanged?: Maybe<boolean>;
    changedCondition?: Maybe<string>;
    roomNumChanged?: Maybe<boolean>;
  }) => {
    this.setState(
      {
        rooms,
        adults,
        childrenAges,
      },
      () => {
        if (this.props.shouldAutoSearch) {
          this.traceConditionChangedEvent({
            adultChanged,
            changedCondition: CHANGED_CONDITION.GUEST_OR_ROOMS,
            childrenAgesChanged,
            roomNumChanged,
          });
          this.triggerSearch();
        }
      },
    );
  };

  getGuestsRoomsChildrenNoAutoSearch = ({
    adultChanged,
    adults,
    childrenAges,
    childrenAgesChanged,
    roomNumChanged,
    rooms,
  }: {
    rooms: number;
    adults: number;
    childrenAges: number[];
    adultChanged?: Maybe<boolean>;
    childrenAgesChanged?: Maybe<boolean>;
    changedCondition?: Maybe<string>;
    roomNumChanged?: Maybe<boolean>;
  }) => {
    this.setState(
      {
        rooms,
        adults,
        childrenAges,
      },
      () => {
        this.traceConditionChangedEvent({
          adultChanged,
          changedCondition: CHANGED_CONDITION.GUEST_OR_ROOMS,
          childrenAgesChanged,
          roomNumChanged,
        });
      },
    );
  };

  traceConditionChangedEvent = ({
    adultChanged,
    changedCondition,
    childrenAgesChanged,
    roomNumChanged,
  }: {
    adultChanged?: Maybe<boolean>;
    childrenAgesChanged?: Maybe<boolean>;
    changedCondition: string;
    roomNumChanged?: Maybe<boolean>;
  }) => {
    if (this.props.layout === LAYOUT.CHAPPED) {
      const { childrenAges, destination, rooms } = this.state;
      this.props.miniEventTracker.trackHotelsAction(
        ACTION_TYPE.SEARCH_CONDITION_CHANGED,
        buildAdditionalInfoSearchConditionChanged({
          hotelId: destination && destination.entityId,
          changedCondition,
          adultChanged,
          childrenAgesChanged,
          roomNumChanged,
          rooms,
          isSearchWithChildren: !!childrenAges && childrenAges.length > 0,
        }),
      );
    }
  };

  onSubmitClick = () => {
    const { miniEventTracker } = this.props;
    try {
      logger.event(SEARCH_CLICKED);
      const { destination, miniEventFilters } = this.state;
      const searchWithChildren: boolean =
        this.state.childrenAges && this.state.childrenAges.length > 0;

      if (!destination) {
        return;
      }
      const { entity, entityId, type } = destination;
      const additionalInfoDayViewSearch = {
        entityID: entityId ? parseInt(entityId, 10) : undefined,
        entityName: entity,
        isSearchWithChildren: searchWithChildren,
        entityType: type && ENTITY_TYPE[type.toUpperCase()],
        filters: null,
      };

      if (miniEventFilters) {
        additionalInfoDayViewSearch.filters = miniEventFilters;
      }

      const actionType = SEARCH_ACTION_TYPE[this.props.userContext.pageType];
      if (!actionType) {
        // TODO: we may also need to track the search action on the details page
        return;
      }
      miniEventTracker.trackHotelsAction(
        actionType,
        buildAdditionalInfoDayViewSearch(additionalInfoDayViewSearch),
      );

      miniEventTracker.trackSmartMetric(actionType);
    } catch (e) {
      // Quietly catching all errors to make sure the form is submitted
    }
  };

  onFormSubmit = (e?: any) => {
    if (e) {
      e.preventDefault();
    }

    const {
      i18n: { formatDate },
      onSubmit,
    } = this.props;
    if (this.validateForm()) {
      const {
        adults,
        checkInDate,
        checkOutDate,
        childrenAges,
        destination,
        freeCancellation,
        rooms,
        urlFilters,
      } = this.state;

      this.searchItems.unshift({
        destination,
        checkInDate,
        checkOutDate,
        adults,
        childrenAges,
        rooms,
      });
      localStorage.trySetValue(
        'recentSearchItems',
        JSON.stringify(this.searchItems.slice(0, 5)),
      );

      logger.event(SEARCH_SUMITTED);
      onSubmit(
        {
          destination,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          numberOfAdults: adults,
          numberOfRooms: rooms,
          childrenAges,
          freeCancellation,
          filters: urlFilters,
        },
        formatDate,
      );
    } else {
      logger.event(SEARCH_VALIDATION_FAILED);
    }
  };

  triggerSearch = () => {
    this.onSubmitClick();
    this.onFormSubmit();
  };

  getPreference = (filters: any) => {
    const { miniEventFilters, urlFilters } = filters;
    const urlFilterKeys = Object.keys(urlFilters);
    this.setState({
      urlFilters: urlFilterKeys.length > 0 ? urlFilters : null,
      miniEventFilters: urlFilterKeys.length > 0 ? miniEventFilters : null,
    });
  };

  onSuggestionSelected = (item: SearchDestination) => {
    this.onDestinationChange(item);
    const { isFromRecentSearch } = item;
    if (isFromRecentSearch) {
      this.onRecentSearchesChange(item);
    }
    if (this.props.shouldAutoSearch || isFromRecentSearch) {
      setTimeout(() => {
        this.triggerSearch();
      }, 0);
    }
  };

  updateMbdProps() {
    const {
      adults,
      checkInDate,
      checkOutDate,
      childrenAges,
      destination,
      rooms,
    } = this.state;
    const {
      i18n: { formatDate },
      userContext,
    } = this.props;

    this.mbdProps = buildSearchSubmitButtonAttributes({
      userContext,
      formatDate,
      destination,
      checkInDate,
      checkOutDate,
      adults,
      childrenAges,
      rooms,
    });
  }

  validateForm() {
    const { destination } = this.state;
    const hasValue = hasDestination(destination);
    this.setState({
      validDestination: hasValue,
    });
    return hasValue;
  }

  render() {
    const {
      arrangeInline,
      className,
      destinationLabel,
      freeCancellation,
      hideLabel,
      i18n: { translate },
      isFromSeoPage,
      layout,
      lightLabel,
      masonry,
      shouldAutoSearch,
      stay,
      submittingForm,
    } = this.props;
    const {
      adults,
      checkInDate,
      checkOutDate,
      childrenAges,
      destination,
      destinationInput,
      rooms,
      showPreference,
      validDestination,
    } = this.state;

    const noCheckDates = !stay || (stay && !stay.checkIn && !stay.checkOut);

    return (
      <>
        {!validDestination && (
          <div
            className={cls(
              arrangeInline
                ? 'SearchControls__destinationErr--inline'
                : 'SearchControls__destinationErr--default',
              noCheckDates && 'SearchControls__destinationErr--noMargin',
            )}
          >
            <BpkText
              className={cls('SearchControls__destinationErr--message')}
              color={TEXT_COLORS.textPrimary}
            >
              {translate('SearchControls_label_Destination_error')}
            </BpkText>
          </div>
        )}
        <form
          data-test-id="search-controls"
          onSubmit={this.onFormSubmit}
          className={cls(
            arrangeInline && 'SearchControls',
            layout === LAYOUT.CHAPPED ? 'SearchControls__chapped' : null,
            className,
          )}
        >
          <AutosuggestDataProvider input={destinationInput}>
            {(suggestions, onClearSuggestions) => {
              if (layout === LAYOUT.HORIZONTAL) {
                return (
                  <HorizontalLayout
                    suggestions={suggestions}
                    destination={destination}
                    destinationLabel={destinationLabel}
                    submittingForm={submittingForm}
                    lightLabel={lightLabel}
                    checkInDate={checkInDate}
                    checkOutDate={checkOutDate}
                    adults={adults}
                    rooms={rooms}
                    childrenAges={childrenAges}
                    onChangeInputValue={this.onChangeInputValue}
                    onDatesRangeApply={this.onDatesRangeApply}
                    onSuggestionsFetchRequested={
                      this.onDestinationSuggestionsRequested
                    }
                    onSuggestionSelected={this.onSuggestionSelected}
                    getGuestsRoomsChildren={this.getGuestsRoomsChildren}
                    onClearSuggestions={onClearSuggestions}
                    validDestination={validDestination ? null : false}
                    showSearchButton={!shouldAutoSearch}
                    arrangeInline={arrangeInline}
                    hideLabel={hideLabel}
                  />
                );
              }

              if (layout === LAYOUT.CHAPPED) {
                return (
                  <ChappedLayoutSEO
                    lightLabel={lightLabel}
                    checkInDate={checkInDate}
                    checkOutDate={checkOutDate}
                    adults={adults}
                    rooms={rooms}
                    childrenAges={childrenAges}
                    onDatesChanged={this.onDatesRangeApplyNoAutoSearch}
                    onSubmit={this.triggerSearch}
                    getGuestsRoomsChildren={
                      this.getGuestsRoomsChildrenNoAutoSearch
                    }
                  />
                );
              }

              return (
                <ExpandableLayout
                  mbdProps={this.mbdProps}
                  suggestions={suggestions}
                  destination={destination}
                  destinationLabel={destinationLabel}
                  submittingForm={submittingForm}
                  lightLabel={lightLabel}
                  checkInDate={checkInDate}
                  checkOutDate={checkOutDate}
                  adults={adults}
                  rooms={rooms}
                  childrenAges={childrenAges}
                  onSuggestionsFetchRequested={
                    this.onDestinationSuggestionsRequested
                  }
                  onSuggestionSelected={this.onSuggestionSelected}
                  getGuestsRoomsChildren={this.getGuestsRoomsChildren}
                  onDatesChanged={this.onDatesChanged}
                  onClearSuggestions={onClearSuggestions}
                  onGetPreference={this.getPreference}
                  onSubmitClick={this.onSubmitClick}
                  showPreference={!!showPreference}
                  freeCancellation={freeCancellation}
                  arrangeInline={arrangeInline}
                  masonry={masonry}
                  validDestination={validDestination ? null : false}
                  isFromSeoPage={isFromSeoPage}
                />
              );
            }}
          </AutosuggestDataProvider>
        </form>
      </>
    );
  }
}

export default withMiniEventTracker(withI18n(withUserContext(SearchControls)));
export { LAYOUT };
